/**
 * XLS Import — browser-based Excel flight logbook import.
 * Reuses pure functions from scripts/migrate-excel.js for parsing and validation.
 * Dedup-checks via shared flight-dedup.js to skip already-imported rows.
 *
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.5, 19.1, 19.2, 20.1, 20.2, 20.3
 */

import * as XLSX from 'xlsx';
import {
  mapExcelRow,
  validateExcelRow,
  parseTimeToMins,
  mapFuelLevel,
  normalisePilotName,
  DEFAULT_COL_MAPPING,
  excelSerialToDate,
} from '../../utils/excel-helpers.js';
import { buildDedupKey, findExistingFlights } from '../../utils/flight-dedup.js';
import { getAircraft, getPilots, createFlight, createFuelOil, updatePilot } from '../../services/api.js';
import { resolveImportPilotId } from '../../utils/alias-resolution.js';
import { getSupabase } from '../../services/supabase.js';
import { openDetailModal } from '../detail-modal.js';

// ── Pure helpers (exported for testing) ─────────────────────────────────────

/**
 * Build the XLS template structure: headers and one example row.
 * Headers match the fixed Excel column mapping (F–AC).
 * Mandatory columns are prefixed with '*'.
 * @returns {{ headers: string[], exampleRow: Object }}
 */
export function buildXLSTemplate() {
  const headers = [
    '*flight_date',
    '*aircraft',
    '*pilot',
    'departure',
    'destination',
    '*block_time_mins',
    'flight_time_mins',
    'main_tank_level',
    'right_wing_level',
    'belly_tank_level',
    'left_wing_level',
    'notes',
    'fuel_uplift',
    'fuel_account_type',
    'oil_level',
    'departure_time',
    'arrival_time',
    'ifr_time_mins',
    'takeoffs',
    'landings',
    'who_pays',
    'p2_name',
    'purpose',
    'fuel_uplift_cost_currency',
  ];

  const exampleRow = {
    '*flight_date': '2024-01-15',
    '*aircraft': 'G-ABCD',
    '*pilot': 'John Smith',
    'departure': 'EGLL',
    'destination': 'EGBB',
    '*block_time_mins': '1:30',
    'flight_time_mins': '1:15',
    'main_tank_level': 'Full',
    'right_wing_level': '3/4',
    'belly_tank_level': '1/2',
    'left_wing_level': '3/4',
    'notes': 'Training flight',
    'fuel_uplift': '50L @ £1.80',
    'fuel_account_type': 'ACCOUNT',
    'oil_level': '6',
    'departure_time': '09:15',
    'arrival_time': '10:30',
    'ifr_time_mins': '0:25',
    'takeoffs': 1,
    'landings': 1,
    'who_pays': 'CAPTAIN',
    'p2_name': 'Jane Doe',
    'purpose': 'Training',
    'fuel_uplift_cost_currency': 'GBP',
  };

  return { headers, exampleRow };
}

/**
 * Return column metadata for the import template.
 * Used by both the downloadable template and the on-page column reference.
 * @returns {{ name: string, required: boolean, description: string }[]}
 */
export function getColumnReference() {
  return [
    { name: 'flight_date',              required: true,  description: 'Date of flight (YYYY-MM-DD)' },
    { name: 'aircraft',                 required: true,  description: 'Aircraft name or registration' },
    { name: 'pilot',                    required: true,  description: 'Pilot name' },
    { name: 'departure',                required: false, description: 'Departure airfield' },
    { name: 'destination',              required: false, description: 'Destination airfield' },
    { name: 'block_time_mins',          required: true,  description: 'Block time (h:mm or decimal hours)' },
    { name: 'flight_time_mins',         required: false, description: 'Flight time (h:mm or decimal hours)' },
    { name: 'main_tank_level',          required: false, description: 'Fuel level: Full, 3/4, 2/3, 1/2, 1/3, 1/4, Empty' },
    { name: 'right_wing_level',         required: false, description: 'Right wing fuel level' },
    { name: 'belly_tank_level',         required: false, description: 'Belly tank fuel level' },
    { name: 'left_wing_level',          required: false, description: 'Left wing fuel level' },
    { name: 'notes',                    required: false, description: 'Free text notes' },
    { name: 'fuel_uplift',              required: false, description: 'Fuel uplift (e.g. "50L @ £1.80")' },
    { name: 'fuel_account_type',        required: false, description: 'ACCOUNT, OVERSEAS_ACCOUNT, or NON_ACCOUNT' },
    { name: 'oil_level',               required: false, description: 'Oil level (numeric)' },
    { name: 'departure_time',           required: false, description: 'Actual departure time (HH:MM)' },
    { name: 'arrival_time',             required: false, description: 'Actual arrival time (HH:MM)' },
    { name: 'ifr_time_mins',            required: false, description: 'IFR time (h:mm or decimal hours)' },
    { name: 'takeoffs',                 required: false, description: 'Number of takeoffs (integer)' },
    { name: 'landings',                 required: false, description: 'Number of landings (integer)' },
    { name: 'who_pays',                 required: false, description: 'CAPTAIN or P2' },
    { name: 'p2_name',                  required: false, description: 'P2 pilot name' },
    { name: 'purpose',                  required: false, description: 'Flight purpose (e.g. Training)' },
    { name: 'fuel_uplift_cost_currency', required: false, description: 'Currency code (e.g. GBP)' },
  ];
}

/**
 * Format an import summary for display.
 * @param {number} imported - Count of successfully imported rows
 * @param {number} skipped - Count of dedup-skipped rows
 * @param {number} failed - Count of failed rows
 * @param {string[]} errors - Per-row error messages
 * @returns {string}
 */
export function formatXLSImportSummary(imported, skipped, failed, errors = []) {
  const parts = [
    `Imported: ${imported}`,
    `Skipped (already exist): ${skipped}`,
    `Failed: ${failed}`,
  ];

  let summary = parts.join(' | ');

  if (errors.length > 0) {
    summary += '\n\nErrors:\n' + errors.map((e) => `  • ${e}`).join('\n');
  }

  return summary;
}

/**
 * Collect pilot names from validRows that are not present in pilotMap.
 * Returns a Map of lowercased-normalised name → display-cased name,
 * automatically deduplicating by the lowercased key.
 * @param {Object[]} validRows - Array of mapped/validated XLS rows with a .pilot field
 * @param {Map<string, string>} pilotMap - Map of lowercased pilot name → pilot UUID
 * @returns {Map<string, string>} Missing pilots: lowercased key → display name
 */
export function collectMissingPilots(validRows, pilotMap) {
  const missing = new Map();
  for (const row of validRows) {
    const normalised = normalisePilotName(row.pilot);
    const key = normalised.toLowerCase();
    if (!pilotMap.has(key) && !missing.has(key)) {
      missing.set(key, normalised);
    }
  }
  return missing;
}

/**
 * Show a modal dialog listing missing pilots with checkboxes.
 * Resolves with a Set of selected lowercased pilot keys on confirm,
 * or null if the user cancels.
 * @param {Map<string, string>} missingPilots - Map of lowercased key → display name
 * @returns {Promise<Set<string> | null>}
 */
export function showMissingPilotsModal(missingPilots) {
  return new Promise((resolve) => {
    // Wrapper for select all/none controls + grid
    const wrapper = document.createElement('div');
    wrapper.className = 'missing-pilots-checklist-wrapper';

    // Select All / None controls
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;gap:0.75rem;margin-bottom:0.5rem;font-size:0.85rem;';

    const selectAllBtn = document.createElement('button');
    selectAllBtn.type = 'button';
    selectAllBtn.className = 'btn btn--link';
    selectAllBtn.textContent = 'Select All';
    selectAllBtn.style.cssText = 'padding:0;background:none;border:none;color:var(--color-primary,#2563eb);cursor:pointer;text-decoration:underline;font-size:inherit;';

    const selectNoneBtn = document.createElement('button');
    selectNoneBtn.type = 'button';
    selectNoneBtn.className = 'btn btn--link';
    selectNoneBtn.textContent = 'Select None';
    selectNoneBtn.style.cssText = 'padding:0;background:none;border:none;color:var(--color-primary,#2563eb);cursor:pointer;text-decoration:underline;font-size:inherit;';

    controls.appendChild(selectAllBtn);
    controls.appendChild(selectNoneBtn);
    wrapper.appendChild(controls);

    // Build 3-column checkbox grid (name on left, checkbox on right)
    const container = document.createElement('div');
    container.className = 'missing-pilots-checklist';
    container.style.cssText = 'display:grid;grid-template-columns:repeat(3,1fr);gap:0.35rem 1rem;';

    selectAllBtn.addEventListener('click', () => {
      container.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = true; });
    });
    selectNoneBtn.addEventListener('click', () => {
      container.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
    });

    for (const [key, displayName] of missingPilots) {
      const label = document.createElement('label');
      label.className = 'missing-pilots-checklist__item';
      label.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:0.5rem;padding:0.25rem 0.4rem;cursor:pointer;';

      const span = document.createElement('span');
      span.textContent = displayName;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = true;
      checkbox.dataset.pilotKey = key;

      label.appendChild(span);
      label.appendChild(checkbox);
      container.appendChild(label);
    }

    wrapper.appendChild(container);

    openDetailModal({
      title: 'Missing Pilots',
      fields: [],
      customSections: [wrapper],
      saveLabel: 'Create & Continue',
      onSave: () => {
        const selected = new Set();
        const checkboxes = container.querySelectorAll('input[type="checkbox"]');
        for (const cb of checkboxes) {
          if (cb.checked) {
            selected.add(cb.dataset.pilotKey);
          }
        }
        resolve(selected);
      },
      onCancel: () => {
        resolve(null);
      },
    });
  });
}

/**
 * Create placeholder pilot records for user-selected missing pilots.
 * Uses the rpc_create_import_pilot database function which handles both
 * auth.users and pilots table inserts server-side with elevated privileges.
 * @param {Set<string>} selectedNames - Set of lowercased pilot keys the user selected
 * @param {Map<string, string>} missingPilots - Map of lowercased key → display name
 * @returns {Promise<Map<string, string>>} Map of lowercased pilot name → created pilot UUID
 */
export async function createSelectedPilots(selectedNames, missingPilots) {
  const supabase = getSupabase();
  const created = new Map();

  for (const key of selectedNames) {
    const displayName = missingPilots.get(key);
    if (!displayName) continue;

    // Generate email from display name
    const tokens = displayName.toLowerCase().split(/\s+/).map(t => t.replace(/[^a-z]/g, '')).filter(Boolean);
    const first = tokens[0] || 'pilot';
    const last = tokens.length > 1 ? tokens[tokens.length - 1] : first;
    const email = `${first}.${last}@imported.local`;

    const { data: pilotId, error } = await supabase.rpc('rpc_create_import_pilot', {
      p_name: displayName,
      p_email: email,
      p_role: 'pilot',
    });
    if (error) throw error;

    // Set guest flag on newly created imported pilot (Requirement 11.1)
    await updatePilot(pilotId, { guest: true });

    created.set(key, pilotId);
  }

  return created;
}

// ── Column validation ───────────────────────────────────────────────────────

/**
 * Validate that the uploaded XLS file has data in the expected column positions.
 * Checks the first data row for non-empty values in the key columns (F, G, H, K).
 *
 * @param {Object[]} rows - Raw rows from sheet_to_json (keyed by column letter)
 * @returns {{ valid: boolean, message: string }}
 */
function validateColumnPositions(rows) {
  if (!rows || rows.length === 0) {
    return { valid: false, message: 'No data rows found in the uploaded file.' };
  }

  // Check first few rows (skip potential header row)
  const checkRow = rows.length > 1 ? rows[1] : rows[0];

  // At minimum, columns F (date), G (aircraft), H (pilot) should have data
  const hasDate = checkRow.F != null && checkRow.F !== '';
  const hasAircraft = checkRow.G != null && checkRow.G !== '';
  const hasPilot = checkRow.H != null && checkRow.H !== '';

  if (!hasDate && !hasAircraft && !hasPilot) {
    return {
      valid: false,
      message: 'Column layout does not match expected format. Download the template to see the expected layout.',
    };
  }

  return { valid: true, message: '' };
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Creates the XLS import panel.
 * Reuses pure functions from scripts/migrate-excel.js for parsing and validation.
 * @returns {HTMLElement}
 */
export function createXLSImport() {
  const section = document.createElement('section');
  section.className = 'settings-section xls-import';

  const heading = document.createElement('h2');
  heading.textContent = 'Import Flights XLS';
  section.appendChild(heading);

  const description = document.createElement('p');
  description.className = 'xls-import__description';
  description.textContent =
    'Upload an Excel file (.xls or .xlsx) with flight data in the expected column layout. ' +
    'Rows that already exist in the database will be skipped automatically.';
  section.appendChild(description);

  // Column reference table
  const colRef = document.createElement('details');
  colRef.open = true;
  colRef.className = 'xls-import__column-ref';

  const colRefSummary = document.createElement('summary');
  colRefSummary.textContent = 'Expected Columns';
  colRef.appendChild(colRefSummary);

  const columns = getColumnReference();
  const grid = document.createElement('div');
  grid.className = 'column-ref-grid';

  // Split columns into two halves for a two-column layout
  const mid = Math.ceil(columns.length / 2);
  const halves = [columns.slice(0, mid), columns.slice(mid)];

  halves.forEach((half) => {
    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    ['Column', 'Req', 'Format'].forEach((text) => {
      const th = document.createElement('th');
      th.textContent = text;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    half.forEach((col) => {
      const tr = document.createElement('tr');

      const tdName = document.createElement('td');
      if (col.required) {
        const strong = document.createElement('strong');
        strong.textContent = `*${col.name}`;
        tdName.appendChild(strong);
      } else {
        tdName.textContent = col.name;
      }
      tr.appendChild(tdName);

      const tdReq = document.createElement('td');
      tdReq.textContent = col.required ? 'Yes' : '';
      tr.appendChild(tdReq);

      const tdFmt = document.createElement('td');
      tdFmt.textContent = col.description;
      tr.appendChild(tdFmt);

      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    grid.appendChild(table);
  });

  colRef.appendChild(grid);
  section.appendChild(colRef);

  // Message area
  const messageArea = document.createElement('div');
  messageArea.className = 'xls-import__message';
  section.appendChild(messageArea);

  // Progress area
  const progressArea = document.createElement('div');
  progressArea.className = 'xls-import__progress';
  progressArea.style.display = 'none';
  section.appendChild(progressArea);

  // Summary area
  const summaryArea = document.createElement('div');
  summaryArea.className = 'xls-import__summary';
  summaryArea.style.display = 'none';
  section.appendChild(summaryArea);

  // Buttons container
  const btnContainer = document.createElement('div');
  btnContainer.className = 'xls-import__buttons';

  // File input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.xls,.xlsx';
  fileInput.className = 'xls-import__file-input';

  // Upload button
  const uploadBtn = document.createElement('button');
  uploadBtn.type = 'button';
  uploadBtn.className = 'btn btn--primary';
  uploadBtn.textContent = 'Import';
  uploadBtn.disabled = true;

  // Download template button
  const templateBtn = document.createElement('button');
  templateBtn.type = 'button';
  templateBtn.className = 'btn btn--secondary';
  templateBtn.textContent = 'Download Template';

  btnContainer.appendChild(fileInput);
  btnContainer.appendChild(uploadBtn);
  btnContainer.appendChild(templateBtn);
  section.appendChild(btnContainer);

  // ── Helpers ─────────────────────────────────────────────────────────────

  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `xls-import__message xls-import__message--${type}`;
  }

  function clearMessage() {
    messageArea.textContent = '';
    messageArea.className = 'xls-import__message';
  }

  function showProgress(text) {
    progressArea.textContent = text;
    progressArea.style.display = 'block';
  }

  function hideProgress() {
    progressArea.textContent = '';
    progressArea.style.display = 'none';
  }

  function setButtonsDisabled(disabled) {
    uploadBtn.disabled = disabled;
    templateBtn.disabled = disabled;
    fileInput.disabled = disabled;
  }

  function showSummary(imported, skipped, failed, errors) {
    summaryArea.innerHTML = '';
    summaryArea.style.display = 'block';

    const title = document.createElement('h3');
    title.textContent = 'Import Summary';
    summaryArea.appendChild(title);

    const counts = document.createElement('div');
    counts.className = 'xls-import__counts';

    const items = [
      { label: 'Imported', value: imported, cls: 'success' },
      { label: 'Skipped (duplicates)', value: skipped, cls: 'info' },
      { label: 'Failed', value: failed, cls: failed > 0 ? 'error' : 'success' },
    ];

    items.forEach(({ label, value, cls }) => {
      const item = document.createElement('span');
      item.className = `xls-import__count xls-import__count--${cls}`;
      item.textContent = `${label}: ${value}`;
      counts.appendChild(item);
    });

    summaryArea.appendChild(counts);

    if (errors.length > 0) {
      const errorList = document.createElement('details');
      const errorSummary = document.createElement('summary');
      errorSummary.textContent = `${errors.length} error(s)`;
      errorList.appendChild(errorSummary);

      const ul = document.createElement('ul');
      ul.className = 'xls-import__errors';
      errors.forEach((err) => {
        const li = document.createElement('li');
        li.textContent = err;
        ul.appendChild(li);
      });
      errorList.appendChild(ul);
      summaryArea.appendChild(errorList);
    }
  }

  // ── File selection ──────────────────────────────────────────────────────

  fileInput.addEventListener('change', () => {
    uploadBtn.disabled = !fileInput.files || fileInput.files.length === 0;
    clearMessage();
    summaryArea.style.display = 'none';
  });

  // ── Template download ───────────────────────────────────────────────────

  templateBtn.addEventListener('click', () => {
    try {
      const { headers, exampleRow } = buildXLSTemplate();

      const wsData = [headers, headers.map((h) => exampleRow[h] || '')];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Template');

      XLSX.writeFile(wb, 'flight-import-template.xlsx');
    } catch (err) {
      showMessage(`Template download failed: ${err.message}`, 'error');
    }
  });

  // ── Import ──────────────────────────────────────────────────────────────

  uploadBtn.addEventListener('click', async () => {
    const file = fileInput.files[0];
    if (!file) return;

    setButtonsDisabled(true);
    clearMessage();
    summaryArea.style.display = 'none';
    showProgress('Reading file…');

    try {
      await performImport(file);
    } catch (err) {
      showMessage(err.message || 'Import failed', 'error');
    } finally {
      hideProgress();
      setButtonsDisabled(false);
      uploadBtn.disabled = true;
    }
  });

  async function performImport(file) {
    // 1. Parse the Excel file in the browser
    let rows;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', raw: true });

      // Use the first sheet (or "Form1" if it exists)
      const sheetName = workbook.SheetNames.includes('Form1')
        ? 'Form1'
        : workbook.SheetNames[0];

      if (!sheetName) {
        throw new Error('No sheets found in the uploaded file.');
      }

      const sheet = workbook.Sheets[sheetName];
      rows = XLSX.utils.sheet_to_json(sheet, { header: 'A', raw: true });
    } catch (err) {
      throw new Error('Unable to parse file — ensure it is a valid .xls or .xlsx file');
    }

    // 2. Validate column positions
    showProgress('Validating column layout…');
    const colCheck = validateColumnPositions(rows);
    if (!colCheck.valid) {
      throw new Error(colCheck.message);
    }

    // 3. Map and validate rows using the same pure functions as the CLI
    showProgress('Processing rows…');
    const dateCol = 'F';
    const validRows = [];
    const errors = [];
    let failCount = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNum = i + 1;

      // Skip header row
      if (i === 0 && row[dateCol] != null && typeof row[dateCol] === 'string' && isNaN(parseFloat(row[dateCol]))) {
        continue;
      }

      // Convert Excel serial date
      row[dateCol] = excelSerialToDate(row[dateCol]);

      const mapped = mapExcelRow(row, DEFAULT_COL_MAPPING);
      const result = validateExcelRow(mapped);

      if (!result.valid) {
        errors.push(`Row ${rowNum}: ${result.errors.join('; ')}`);
        failCount++;
      } else {
        validRows.push({ ...mapped, _rowNum: rowNum });
      }
    }

    if (validRows.length === 0) {
      showSummary(0, 0, failCount, errors);
      showMessage('No valid rows to import.', 'error');
      return;
    }

    // 4. Resolve aircraft_id and pilot_id via API lookups
    showProgress('Resolving aircraft and pilots…');
    const [allAircraft, allPilots] = await Promise.all([getAircraft(), getPilots()]);

    // Build lookup maps (case-insensitive)
    const aircraftMap = new Map();
    for (const ac of allAircraft) {
      if (ac.name) aircraftMap.set(ac.name.toLowerCase(), ac.id);
      if (ac.registration) aircraftMap.set(ac.registration.toLowerCase(), ac.id);
    }

    const pilotMap = new Map();
    for (const p of allPilots) {
      // Resolve aliases to parent UUID for import (Requirement 8.13)
      if (p.name) pilotMap.set(p.name.toLowerCase(), resolveImportPilotId(p));
    }

    // 4a. Collect missing pilots and prompt user if any exist
    const missingPilots = collectMissingPilots(validRows, pilotMap);

    if (missingPilots.size > 0) {
      showProgress('Waiting for missing pilot selection…');
      const selectedNames = await showMissingPilotsModal(missingPilots);

      // User cancelled — abort the entire import
      if (selectedNames === null) {
        showMessage('Import cancelled.', 'info');
        return;
      }

      // Create selected pilots and merge into pilotMap
      if (selectedNames.size > 0) {
        showProgress('Creating missing pilots…');
        const createdPilots = await createSelectedPilots(selectedNames, missingPilots);
        for (const [key, id] of createdPilots) {
          pilotMap.set(key, id);
        }
      }
    }

    // 4b. Resolve IDs for each valid row
    showProgress('Resolving aircraft and pilots…');
    const resolvedRows = [];
    for (const row of validRows) {
      const aircraftKey = (row.aircraft || '').toLowerCase();
      const pilotKey = normalisePilotName(row.pilot).toLowerCase();

      const aircraft_id = aircraftMap.get(aircraftKey);
      const pilot_id = pilotMap.get(pilotKey);

      if (!aircraft_id) {
        errors.push(`Row ${row._rowNum}: Aircraft "${row.aircraft}" not found`);
        failCount++;
        continue;
      }

      if (!pilot_id) {
        errors.push(`Row ${row._rowNum}: Pilot "${row.pilot}" not created`);
        failCount++;
        continue;
      }

      resolvedRows.push({ ...row, aircraft_id, pilot_id });
    }

    if (resolvedRows.length === 0) {
      showSummary(0, 0, failCount, errors);
      showMessage('No rows could be resolved to existing aircraft/pilots.', 'error');
      return;
    }

    // 5. Dedup check
    showProgress('Checking for duplicates…');
    let existingKeys;
    try {
      existingKeys = await findExistingFlights(resolvedRows);
    } catch (err) {
      throw new Error(`Dedup check failed: ${err.message}`);
    }

    // Partition into new vs duplicate rows
    const newRows = [];
    let skippedCount = 0;

    for (const row of resolvedRows) {
      const key = buildDedupKey(row);
      if (existingKeys.has(key)) {
        skippedCount++;
      } else {
        newRows.push(row);
      }
    }

    if (newRows.length === 0) {
      showSummary(0, skippedCount, failCount, errors);
      showMessage('All rows already exist in the database — nothing to import.', 'info');
      return;
    }

    // 6. Insert new rows
    showProgress(`Importing ${newRows.length} flights…`);
    let importedCount = 0;

    for (const row of newRows) {
      try {
        const flightPayload = {
          flight_date: row.flight_date,
          aircraft_id: row.aircraft_id,
          pilot_id: row.pilot_id,
          departure: row.departure || null,
          destination: row.destination || null,
          block_time_mins: row.block_time_mins,
          flight_time_mins: isNaN(row.flight_time_mins) ? null : row.flight_time_mins,
          notes: row.notes || null,
          captain_name: row.pilot,
          status: 'FLOWN',
          source: 'XLS',
          log_complete: true,
          start_time: '09:00',
          end_time: (() => {
            const blockMins = row.block_time_mins || 60;
            const endMins = 9 * 60 + blockMins;
            const h = Math.min(Math.floor(endMins / 60), 23);
            const m = endMins % 60;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          })(),
          departure_time: row.departure_time || null,
          arrival_time: row.arrival_time || null,
          ifr_time_mins: row.ifr_time_mins ?? null,
          takeoffs: row.takeoffs ?? null,
          landings: row.landings ?? null,
          who_pays: row.who_pays || null,
          p2_name: row.p2_name || null,
          purpose: row.purpose || null,
        };

        const flight = await createFlight(flightPayload);

        // Insert fuel/oil record if any fuel data is present
        if (
          row.main_tank_level ||
          row.left_wing_level ||
          row.right_wing_level ||
          row.belly_tank_level ||
          row.fuel_uplift_litres ||
          row.fuel_uplift_cost != null ||
          row.fuel_account_type ||
          row.fuel_uplift_cost_currency ||
          row.oil_level != null
        ) {
          await createFuelOil({
            flight_id: flight.id,
            main_tank_level: row.main_tank_level || null,
            left_wing_level: row.left_wing_level || null,
            right_wing_level: row.right_wing_level || null,
            belly_tank_level: row.belly_tank_level || null,
            fuel_uplift_litres: row.fuel_uplift_litres || null,
            fuel_uplift_cost: row.fuel_uplift_cost || null,
            fuel_account_type: row.fuel_account_type || null,
            fuel_uplift_cost_currency: row.fuel_uplift_cost_currency || null,
            oil_level: row.oil_level || null,
          });
        }

        importedCount++;
      } catch (err) {
        errors.push(`Row ${row._rowNum}: ${err.message}`);
        failCount++;
      }
    }

    // 7. Display summary
    showSummary(importedCount, skippedCount, failCount, errors);

    if (importedCount > 0) {
      showMessage(`Successfully imported ${importedCount} flight(s).`, 'success');
    } else {
      showMessage('Import completed with errors — see summary below.', 'error');
    }
  }

  return section;
}
